/** The app either renders its summary within a few seconds of load or not at all. */
const SUMMARY_WATCH_TIMEOUT = 15000;
const WRITE_REVIEW_PARAM = 'melaxin_write_review';
const AG_REVIEWS_ENDPOINT = 'https://app.airreviews.io/review/submit';
const AG_SHOP_ID_FALLBACK = 'aP6hvChh9jB0ljViQ5lE';

class MelaxinReviewSummary extends HTMLElement {
  connectedCallback() {
    this.prepareAirReviewsContext();
    this.querySelector('[data-write-review]')?.addEventListener('click', this.openReviewForm);
    this.filterDetails = [...this.querySelectorAll('.melaxin-review-filter')];
    this.filterDetails.forEach((filter) => {
      filter.addEventListener('toggle', this.handleFilterToggle);
    });
    this.querySelectorAll('[data-review-filter-option]').forEach((option) => {
      option.addEventListener('click', this.selectReviewFilter);
    });
    this.openRequestedReviewForm();
    if (this.syncFromAirReviews()) return;

    // Only watch when there is a rating breakdown here to fill. A summary rendered without
    // one has nothing to sync, and watching the whole document for it anyway is not free:
    // the callback runs on every DOM change for the life of the page, and the review list
    // below inserts a hundred cards.
    if (!this.querySelector('[data-review-rating]')) return;

    this.observer = new MutationObserver(() => {
      if (this.syncFromAirReviews()) this.stopWatching();
    });
    this.observer.observe(document.body, { childList: true, subtree: true });
    // Stop either way, so a page where the app never renders a summary is not left
    // observing every mutation until the visitor leaves.
    this.watchTimeout = window.setTimeout(this.stopWatching, SUMMARY_WATCH_TIMEOUT);
  }

  disconnectedCallback() {
    this.querySelector('[data-write-review]')?.removeEventListener('click', this.openReviewForm);
    this.filterDetails?.forEach((filter) => {
      filter.removeEventListener('toggle', this.handleFilterToggle);
    });
    this.querySelectorAll('[data-review-filter-option]').forEach((option) => {
      option.removeEventListener('click', this.selectReviewFilter);
    });
    if (this.writeReviewTimer) window.clearInterval(this.writeReviewTimer);
    this.writeReviewTimer = null;
    if (this.airContextTimer) window.clearInterval(this.airContextTimer);
    this.airContextTimer = null;
    this.stopWatching();
  }

  stopWatching = () => {
    this.observer?.disconnect();
    this.observer = null;
    if (this.watchTimeout) window.clearTimeout(this.watchTimeout);
    this.watchTimeout = null;
  };

  openReviewForm = () => {
    if (this.clickAirReviewTrigger()) return;
    this.openAgReviewDialog();
  };

  handleFilterToggle = (event) => {
    const openedFilter = event.currentTarget;
    if (!(openedFilter instanceof HTMLDetailsElement) || !openedFilter.open) return;

    this.filterDetails?.forEach((filter) => {
      if (filter !== openedFilter && filter.open) filter.open = false;
    });
  };

  selectReviewFilter = (event) => {
    const option = event.currentTarget;
    if (!(option instanceof HTMLElement)) return;

    const menu = option.closest('.melaxin-review-filter');
    const label = menu?.querySelector('[data-review-filter-label]');
    if (label) label.textContent = option.dataset.filterLabel || option.textContent?.trim() || '';
    menu?.removeAttribute('open');

    this.dispatchEvent(
      new CustomEvent('melaxin:review-filter', {
        bubbles: true,
        detail: {
          sectionId: this.dataset.sectionId,
          kind: option.dataset.filterKind,
          value: option.dataset.filterValue,
        },
      })
    );
  };

  openAgReviewDialog() {
    let dialog = document.querySelector('[data-ag-review-dialog]');
    if (!(dialog instanceof HTMLDialogElement)) {
      dialog = document.createElement('dialog');
      dialog.className = 'melaxin-ag-review-dialog';
      dialog.setAttribute('data-ag-review-dialog', '');
      dialog.innerHTML = `
        <form class="melaxin-ag-review-form">
          <button class="melaxin-ag-review-form__close" type="button" aria-label="Schließen">&times;</button>
          <h2>Bewertung schreiben</h2>
          <fieldset class="melaxin-ag-review-form__rating">
            <legend>Wie gefällt dir dieses Produkt?</legend>
            <div class="melaxin-ag-review-form__stars">
              ${[5, 4, 3, 2, 1]
                .map(
                  (rating) => `<input id="melaxin-rating-${rating}" name="rate" type="radio" value="${rating}" ${rating === 5 ? 'required' : ''}><label for="melaxin-rating-${rating}" aria-label="${rating} Sterne">★</label>`
                )
                .join('')}
            </div>
          </fieldset>
          <label>Deine Bewertung<textarea name="content" rows="5" required></textarea></label>
          <label>Dein Name<input name="name" autocomplete="name" required></label>
          <label>Deine E-Mail<input name="email" type="email" autocomplete="email" required></label>
          <p class="melaxin-ag-review-form__policy">Mit dem Absenden stimmst du zu, dass deine Bewertung öffentlich veröffentlicht werden kann.</p>
          <p class="melaxin-ag-review-form__message" role="status" aria-live="polite"></p>
          <button class="melaxin-ag-review-form__submit" type="submit">Bewertung absenden</button>
          <div class="melaxin-ag-review-form__state" aria-live="polite" aria-atomic="true">
            <span class="melaxin-ag-review-form__plane" aria-hidden="true">➤</span>
            <span class="melaxin-ag-review-form__check" aria-hidden="true">✓</span>
            <strong data-review-state-title>Bewertung wird gesendet</strong>
            <span data-review-state-copy>Einen kleinen Moment…</span>
          </div>
        </form>`;
      const style = document.createElement('style');
      style.textContent = `
        .melaxin-ag-review-dialog{width:min(560px,calc(100vw - 24px));max-width:none;max-height:calc(100dvh - 24px);padding:0;border:0;border-radius:16px;background:#fff;color:#171717;box-shadow:0 18px 70px rgba(0,0,0,.3)}
        .melaxin-ag-review-dialog::backdrop{background:rgba(0,0,0,.55)}
        .melaxin-ag-review-form{position:relative;display:grid;min-height:440px;gap:16px;padding:28px;overflow:auto}
        .melaxin-ag-review-form h2{margin:0;padding-right:42px;font-size:24px}
        .melaxin-ag-review-form label,.melaxin-ag-review-form legend{font-weight:650}
        .melaxin-ag-review-form input:not([type=radio]),.melaxin-ag-review-form textarea{display:block;width:100%;margin-top:7px;padding:12px;border:1px solid #d7d2d5;border-radius:8px;background:#fff;color:#171717;font:inherit}
        .melaxin-ag-review-form__close{position:absolute;top:14px;right:14px;width:38px;height:38px;padding:0;border:1px solid #ddd;border-radius:50%;background:#fff;color:#171717;font-size:28px;line-height:1;cursor:pointer}
        .melaxin-ag-review-form__rating{margin:0;padding:0;border:0}
        .melaxin-ag-review-form__stars{display:flex;width:max-content;flex-direction:row-reverse;margin-top:8px}
        .melaxin-ag-review-form__stars input{position:absolute;opacity:0;pointer-events:none}
        .melaxin-ag-review-form__stars label{padding:0 3px;color:#d8d8d8;font-size:34px;line-height:1;cursor:pointer}
        .melaxin-ag-review-form__stars label:hover,.melaxin-ag-review-form__stars label:hover~label,.melaxin-ag-review-form__stars input:checked~label{color:#f5c518}
        .melaxin-ag-review-form__policy,.melaxin-ag-review-form__message{margin:0;font-size:12px;line-height:1.45}
        .melaxin-ag-review-form__message{color:#9b0b60;font-weight:650}
        .melaxin-ag-review-form__submit{min-height:48px;border:0;border-radius:999px;background:#9b0b60;color:#fff;font:inherit;font-weight:750;cursor:pointer}
        .melaxin-ag-review-form__submit[disabled]{opacity:.6;cursor:wait}
        .melaxin-ag-review-form__state{position:absolute;inset:0;z-index:3;display:grid;place-content:center;gap:12px;padding:32px;text-align:center;visibility:hidden;opacity:0;transform:scale(.96);transition:opacity .22s ease,transform .22s ease;background:#fff}
        .melaxin-ag-review-form__state strong{font-size:22px}.melaxin-ag-review-form__state>span:last-child{color:#686168}
        .melaxin-ag-review-form__plane,.melaxin-ag-review-form__check{display:grid;place-items:center;width:76px;height:76px;margin-inline:auto;border-radius:50%;font-size:38px;font-weight:800}
        .melaxin-ag-review-form__plane{background:#9b0b60;color:#fff;transform:rotate(-35deg)}
        .melaxin-ag-review-form__check{display:none;background:#e8f7ed;color:#137333}
        .melaxin-ag-review-form.is-submitting>:not(.melaxin-ag-review-form__state),.melaxin-ag-review-form.is-success>:not(.melaxin-ag-review-form__state){opacity:0;visibility:hidden;pointer-events:none}
        .melaxin-ag-review-form.is-submitting .melaxin-ag-review-form__state,.melaxin-ag-review-form.is-success .melaxin-ag-review-form__state{visibility:visible;opacity:1;transform:scale(1)}
        .melaxin-ag-review-form.is-submitting .melaxin-ag-review-form__plane{animation:melaxin-review-plane 1s ease-in-out infinite}
        .melaxin-ag-review-form.is-success .melaxin-ag-review-form__plane{display:none}.melaxin-ag-review-form.is-success .melaxin-ag-review-form__check{display:grid;animation:melaxin-review-check .42s cubic-bezier(.2,.9,.3,1.3) both}
        @keyframes melaxin-review-plane{50%{transform:translate(18px,-13px) rotate(-35deg)}}@keyframes melaxin-review-check{from{transform:scale(.2)}to{transform:scale(1)}}
        @media(max-width:749px){.melaxin-ag-review-form{padding:24px 18px}.melaxin-ag-review-form h2{font-size:21px}}`;
      document.head.append(style);
      document.body.append(dialog);
      const form = dialog.querySelector('form');
      form?.addEventListener('submit', this.submitAgReview);
      dialog.querySelector('.melaxin-ag-review-form__close')?.addEventListener('click', () => dialog.close());
    }

    dialog.dataset.productJson = this.dataset.productJson || '';
    dialog.dataset.productId = this.dataset.productId || '';
    dialog.dataset.productHandle = this.dataset.productHandle || '';
    dialog.dataset.productTitle = this.dataset.productTitle || '';
    dialog.dataset.productImage = this.dataset.productImage || '';
    const form = dialog.querySelector('form');
    form?.reset();
    form?.classList.remove('is-submitting', 'is-success');
    const stateTitle = form?.querySelector('[data-review-state-title]');
    const stateCopy = form?.querySelector('[data-review-state-copy]');
    if (stateTitle) stateTitle.textContent = 'Bewertung wird gesendet';
    if (stateCopy) stateCopy.textContent = 'Einen kleinen Moment…';
    const message = dialog.querySelector('.melaxin-ag-review-form__message');
    if (message) message.textContent = '';
    dialog.showModal();
  }

  submitAgReview = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    if (!(form instanceof HTMLFormElement)) return;
    const dialog = form.closest('[data-ag-review-dialog]');
    const message = form.querySelector('.melaxin-ag-review-form__message');
    const submit = form.querySelector('[type="submit"]');
    if (!(dialog instanceof HTMLElement)) return;

    const data = new FormData(form);
    const nameParts = String(data.get('name') || '').trim().split(/\s+/);
    let product = {};
    try {
      product = JSON.parse(dialog.dataset.productJson || '{}');
      if (!product || typeof product !== 'object' || Array.isArray(product)) product = {};
    } catch (error) {
      product = {};
    }
    const air = window.AIR_REVIEWS || {};
    const settings = air.settings || {};
    const productId = product.id || Number(dialog.dataset.productId) || undefined;
    const productHandle = product.handle || dialog.dataset.productHandle || '';
    const productTitle = product.title || dialog.dataset.productTitle || '';
    const image = dialog.dataset.productImage || product.featured_image || product.media?.[0]?.preview_image?.src || product.media?.[0]?.src || '';
    const payload = {
      rate: Number(data.get('rate')) || 5,
      content: String(data.get('content') || '').trim(),
      // These are the native AG form field names. Keep the legacy snake_case aliases
      // too: the app accepts both depending on its current submit-form version.
      firstName: nameParts[0] || '',
      lastName: nameParts.slice(1).join(' '),
      first_name: nameParts.shift() || '',
      last_name: nameParts.join(' '),
      email: String(data.get('email') || '').trim(),
      images: [],
      files: [],
      phone: '',
      variants: [],
      // AG Product Reviews stores new storefront submissions in its moderation queue.
      // Its API rejects a missing status as "Invalid review status".
      status: 'disapproved',
      statusBeforeTrash: 'disapproved',
      shopId: settings.shopId || AG_SHOP_ID_FALLBACK,
      shopDomain: settings.shopDomain || window.Shopify?.shop || window.location.hostname,
      productId,
      productHandle,
      productTitle,
      productSku: product.variants?.find((variant) => variant.sku)?.sku || '',
      productImage: image && !String(image).startsWith('http') ? `https:${image}` : image,
      generalSettings: settings.generalSetting || {},
      notificationSource: 'email',
      ignoreContentValidation: Boolean(settings.submitForm?.ignoreContentValidation),
    };

    submit?.setAttribute('disabled', '');
    form.classList.add('is-submitting');
    if (message) message.textContent = '';
    try {
      const response = await fetch(AG_REVIEWS_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json;charset=UTF-8' },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (!response.ok || !result?.success) throw new Error(result?.message || 'Review submission failed');
      form.reset();
      form.classList.replace('is-submitting', 'is-success');
      form.querySelector('[data-review-state-title]').textContent = 'Danke für deine Bewertung!';
      form.querySelector('[data-review-state-copy]').textContent = 'Sie wurde erfolgreich eingereicht.';
      window.setTimeout(() => {
        if (dialog instanceof HTMLDialogElement) dialog.close();
      }, 1800);
    } catch (error) {
      form.classList.remove('is-submitting');
      if (message) message.textContent = 'Die Bewertung konnte nicht gesendet werden. Bitte versuche es erneut.';
    } finally {
      submit?.removeAttribute('disabled');
    }
  };

  waitForAirReviewTrigger() {
    if (this.writeReviewTimer) window.clearInterval(this.writeReviewTimer);
    const deadline = Date.now() + 2000;
    const button = this.querySelector('[data-write-review]');
    button?.setAttribute('aria-busy', 'true');
    this.writeReviewTimer = window.setInterval(() => {
      if (this.clickAirReviewTrigger()) {
        window.clearInterval(this.writeReviewTimer);
        this.writeReviewTimer = null;
        button?.removeAttribute('aria-busy');
      } else if (Date.now() > deadline) {
        window.clearInterval(this.writeReviewTimer);
        this.writeReviewTimer = null;
        button?.removeAttribute('aria-busy');
        this.openOfficialReviewForm();
      }
    }, 250);
  }

  /** AG Product Reviews' own storefront deep-link, used by its review-request emails. */
  openOfficialReviewForm() {
    if (!this.dataset.productReviewsUrl) return;
    const target = new URL(this.dataset.productReviewsUrl, window.location.origin);
    target.search = '';
    target.hash = 'AirReviews-BlockWrapperSubmit?rate=5';
    window.location.assign(target.toString());
  }

  prepareAirReviewsContext() {
    if (!this.dataset.productJson) return;
    try {
      const product = JSON.parse(this.dataset.productJson);
      const applyProduct = () => {
        if (!window.AIR_REVIEWS) return false;
        if (!window.AIR_REVIEWS.product) window.AIR_REVIEWS.product = product;
        return true;
      };
      if (applyProduct()) return;
      const deadline = Date.now() + SUMMARY_WATCH_TIMEOUT;
      this.airContextTimer = window.setInterval(() => {
        if (applyProduct() || Date.now() > deadline) window.clearInterval(this.airContextTimer);
      }, 50);
    } catch (error) {
      // The selected product is absent or malformed; the button simply remains inert.
    }
  }

  /** Opens the app form after arriving from a reviews section without an app block. */
  openRequestedReviewForm() {
    const url = new URL(window.location.href);
    if (url.searchParams.get(WRITE_REVIEW_PARAM) !== '1') return;

    const deadline = Date.now() + SUMMARY_WATCH_TIMEOUT;
    const tryOpen = () => {
      if (this.clickAirReviewTrigger()) {
        url.searchParams.delete(WRITE_REVIEW_PARAM);
        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
        return true;
      }
      return false;
    };

    if (tryOpen()) return;
    this.writeReviewTimer = window.setInterval(() => {
      if (tryOpen() || Date.now() > deadline) window.clearInterval(this.writeReviewTimer);
    }, 250);
  }

  clickAirReviewTrigger() {
    const submitReview = document.querySelector(
      '.AirReviews-Blocks__SubmitReview, .AirReviews-NoReview__SubmitReview, #AirReviews-BlockWrapperSubmit'
    );
    const trigger = submitReview?.matches('button, a, [role="button"]')
      ? submitReview
      : submitReview?.querySelector('button, a, [role="button"]');
    if (!(trigger instanceof HTMLElement)) return false;
    trigger.click();
    return true;
  }

  syncFromAirReviews() {
    const appSummary = document.querySelector('.AirReviews-Blocks__Summary');
    if (!appSummary) return false;

    const counts = new Map();
    for (const item of appSummary.querySelectorAll('.AirReviews-Blocks--ListItem')) {
      const text = item.textContent?.replace(/\s+/g, ' ').trim() ?? '';
      const star = Number(text.match(/^([1-5])\b/)?.[1]);
      const count = Number(text.match(/\(?\s*(\d+)\s*\)?\s*$/)?.[1]);
      if (star && Number.isFinite(count)) counts.set(star, count);
    }

    if (counts.size === 0) return false;
    const maximum = Math.max(...counts.values(), 1);
    for (const row of this.querySelectorAll('[data-review-rating]')) {
      if (!(row instanceof HTMLElement)) continue;
      const star = Number(row.dataset.reviewRating);
      const count = counts.get(star) ?? 0;
      const countElement = row.querySelector('[data-review-count]');
      const bar = row.querySelector('[data-review-bar]');
      if (countElement) countElement.textContent = `(${count})`;
      if (bar instanceof HTMLElement) bar.style.width = `${(count / maximum) * 100}%`;
    }
    return true;
  }
}

if (!customElements.get('melaxin-review-summary')) {
  customElements.define('melaxin-review-summary', MelaxinReviewSummary);
}
